import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthorizationService } from '@modules/authorization/services/authorization.service';
import { LoadDocuments } from '@modules/documents/store/documents.actions';
import {
  SetButtons,
  SetCurrentStep,
  SetFormField,
  SetFormTitle,
  SetProcessId,
} from '@modules/dynamic-form/store/dynamicForm.action';
import { errorsUri } from '@modules/errors/errors.const';
import { ProposalRoutingConstants } from '@modules/order/order.const';
import { CommonApiConstants } from '@modules/shared/constants/commonApi.constants';
import { DictionaryService } from '@modules/shared/services';

import { LoaderService } from '@modules/shared/services/loader.service';

import {
  AddDictionary,
  AuthorizationFinish,
  CommonTypes,
  GetAuthorizationDictionary,
  GetConfirmCode,
  GetConfirmServiceList,
  GetContactsInfo,
  GetUserInfo,
  HandlerConfirmCode,
  HideSpinnerLoading,
  SendSmsConfirmCode,
  SetConfirmationControl,
  SetConfirmServiceList,
  SetConfirmTexts,
  SetContactInfo,
  SetEndTime,
  SetErrorControlById,
  SetMessages,
  SetPhones,
  SetSmsResponseCode,
  SetStartTime,
  SetUserInfo,
  SetValidationsText,
  ShowSpinnerLoading,
  UpdateQuestionnaireProcess,
} from '@modules/shared/store/common.actions';
import { AppState } from '@modules/shared/store/index';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';

import { concatMap, map, mergeMap, switchMap } from 'rxjs/operators';
import { NeedCodeRequest } from '../models';
import { DefaultService } from '@codegen/service';
import { ProcessingResponse } from '@codegen';

@Injectable()
export class CommonEffects {
  private actions$ = inject(Actions);
  private store = inject<Store<AppState>>(Store);
  private defaultService = inject(DefaultService);
  private dictionaryService = inject(DictionaryService);
  private loaderService = inject(LoaderService);
  private authorizationService = inject(AuthorizationService);
  private router = inject(Router);

  public GetConfirmCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetConfirmCode>(CommonTypes.GetConfirmCode),
      map(data => data.payload),
      switchMap((confirmation: NeedCodeRequest) =>
        this.defaultService.confirmNeedCode(confirmation).pipe(
          concatMap(res => {
            if (res.errorInfo.error === '10108') {
              this.router.navigateByUrl(`/${errorsUri.messageLimitExpired}`, {
                state: {
                  actionId: res?.actionId,
                  questionnaireId: res?.questionnaireId,
                },
              });
              return [
                new SetSmsResponseCode(res.errorInfo.error),
                new HandlerConfirmCode(res),
              ];
            } else {
              return [
                new HandlerConfirmCode(res),
                new SetConfirmationControl({
                  controller: res.controller,
                  view: res.view,
                  value: '',
                  id: res.type,
                  isRefreshButton: true,
                }),
                new SetSmsResponseCode(res.errorInfo.error),
                new SetConfirmTexts({
                  header: res.header,
                  message: res.message,
                }),
                new SetValidationsText(res.view?.hint),
                // new HideSpinnerLoading()
              ];
            }
          })
        )
      )
    )
  );

  public GetAuthorizationDictionary$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetAuthorizationDictionary>(
        CommonTypes.GetAuthorizationDictionary
      ),
      map(data => data.payload),
      mergeMap(data => {
        const { fieldId } = data;
        return this.dictionaryService
          .getDictionary(localStorage.getItem('processId'), data.id, data.value)
          .pipe(
            switchMap(res => {
              if (res.errorInfo.error === '10001') {
                return [
                  new AddDictionary({
                    id: res.dictionaryId,
                    items: [],
                    isLoaded: true,
                  }),
                ];
              } else if (res.errorInfo.error === '10000') {
                return [
                  new SetErrorControlById({
                    id: fieldId,
                    errorText: res.errorInfo.errorText,
                  }),
                ];
              } else {
                return [
                  new AddDictionary({
                    id: res.dictionaryId,
                    items: res.dictionaries,
                    isLoaded: true,
                  }),
                ];
              }
            })
          );
      })
    )
  );

  public SendSmsConfirmCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SendSmsConfirmCode>(CommonTypes.SendSmsConfirmCode),
      map(data => data.payload),
      switchMap(data => {
        const { stepType, formValue } = data;
        return this.authorizationService
          .changeQuestionnaireProcessStep(
            localStorage.getItem('processId'),
            stepType,
            {},
            formValue
          )
          .pipe(
            switchMap((res: ProcessingResponse) => {
              if (res.errorInfo.error === '10100') {
                return [new AuthorizationFinish()];
              } else if (res.errorInfo.error === '10102') {
                return [
                  new GetConfirmCode({
                    serviceToConfirm: CommonApiConstants.questionnaireProcess,
                    resend: false,
                  }),
                ];
              } else if (res.errorInfo.error === '11003') {
                localStorage.removeItem('processId');
                this.router.navigate(['']);

                return [
                  new UpdateQuestionnaireProcess({
                    questionnaireId: 'REGISTRATION',
                  }),
                  new SetSmsResponseCode(res.errorInfo.error),
                ];
              } else {
                return [
                  new SetSmsResponseCode(res.errorInfo.error),
                  new SetValidationsText(res.errorInfo.errorText),
                  new HideSpinnerLoading(),
                ];
              }
            })
          );
      })
    )
  );

  public AuthorizationFinish$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AuthorizationFinish>(CommonTypes.AuthorizationFinish),
      switchMap(() => {
        return this.authorizationService.finishAuth().pipe(
          switchMap(() => {
            localStorage.setItem('authorized', 'true');
            localStorage.removeItem('processId');
            localStorage.removeItem('code');
            localStorage.removeItem('authState');

            const defaultRoute = `/order/${ProposalRoutingConstants.proposalsList}`;
            const savedRoute = localStorage.getItem('route');

            this.router.navigateByUrl(savedRoute ? savedRoute : defaultRoute);
            return [new GetConfirmServiceList(), new LoadDocuments()];
          })
        );
      })
    )
  );

  public GetUserInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetUserInfo>(CommonTypes.GetUserInfo),
      switchMap(() => {
        return this.defaultService.informationUserFIO().pipe(
          map(data => {
            return new SetUserInfo({
              firstName: data.firstName,
              lastName: data.lastName,
              middleName: data.middleName,
            });
          })
        );
      })
    )
  );

  public ShowSpinnerLoading$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ShowSpinnerLoading>(CommonTypes.ShowSpinnerLoading),
      map(data => data.payload),
      switchMap((data): Action[] => {
        this.loaderService.start(data);
        return [];
      })
    )
  );

  public HideSpinnerLoading$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HideSpinnerLoading>(CommonTypes.HideSpinnerLoading),
      map(data => data.type),
      switchMap((): Action[] => {
        this.loaderService.stop();
        return [];
      })
    )
  );

  public UpdateQuestionnaireProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateQuestionnaireProcess>(
        CommonTypes.UpdateQuestionnaireProcess
      ),
      map(data => data.payload),
      switchMap(data => {
        if (data.applicationId) {
          return this.defaultService.questionnaireCreateProcess({
            questionnaireId: data.questionnaireId,
            params: { applicationId: data.applicationId },
          });
        } else {
          return this.defaultService.questionnaireCreateProcess({
            questionnaireId: data.questionnaireId,
          });
        }
      }),
      switchMap(response => {
        this.store.dispatch(new SetProcessId(response.processId));
        localStorage.setItem('processId', response.processId);
        return this.defaultService.questionnaireProcess({
          processId: response.processId,
        });
      }),
      switchMap(res => {
        return [
          new SetFormField(res.questionnaireStep.groupList[0].questionList),
          new SetCurrentStep(res.questionnaireStep.hint),
          new SetButtons(res.allowedActions),
          new SetFormTitle(res.questionnaireStep.displayName),
          new HideSpinnerLoading(),
        ];
      })
    )
  );

  public GetConfirmServiceList$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetConfirmServiceList>(CommonTypes.GetConfirmServiceList),
      switchMap(() => this.defaultService.confirmList({})),
      switchMap(response => [new SetConfirmServiceList(response.confirmations)])
    )
  );

  public GetContactsInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetContactsInfo>(CommonTypes.GetContactsInfo),
      switchMap(() =>
        this.defaultService.informationContact().pipe(
          concatMap(res => {
            return [
              new SetContactInfo(res),
              new SetPhones(res.phones),
              new SetMessages(res.messengers),
              new SetStartTime(res.startTime),
              new SetEndTime(res.endTime),
              new HideSpinnerLoading(),
            ];
          })
        )
      )
    )
  );
}
